import { useEffect, useRef } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/** Scrolls a feature row into view inside whatever scrolls it, only as far
 *  as needed ('nearest' does nothing when the row is already fully in
 *  view). Finds the row by its testID, which react-native-web renders as
 *  data-testid. Returns false when the row isn't mounted. */
export function scrollPlanRowIntoView(featureId: string, reducedMotion: boolean): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.querySelector(`[data-testid="plan-feature-${CSS.escape(featureId)}"]`);
  if (!el || typeof el.scrollIntoView !== 'function') return false;
  el.scrollIntoView({ block: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
  return true;
}

/**
 * When a feature becomes selected (a click on its shape on the map), open
 * its bundle if the bundle is collapsed, then scroll its row into view.
 * The bundle's open state lives in each panel as local state, so the panel
 * passes `expandBundle` rather than this hook owning it. A row the user
 * expands or collapses later is left alone: the scroll runs once per new
 * selection.
 */
export function useRevealSelectedPlanRow(
  selectedId: string | null,
  features: readonly { id: string; bundleId?: string | null }[] | undefined,
  isBundleExpanded: (bundleId: string, memberCount: number) => boolean,
  expandBundle: (bundleId: string) => void
): void {
  const reducedMotion = useReducedMotion();
  const pendingRef = useRef<string | null>(null);
  const latest = useRef({ features, isBundleExpanded, expandBundle });
  latest.current = { features, isBundleExpanded, expandBundle };

  const bundleId = selectedId
    ? (features?.find((f) => f.id === selectedId)?.bundleId ?? null)
    : null;
  const memberCount = bundleId ? (features?.filter((f) => f.bundleId === bundleId).length ?? 0) : 0;
  const bundleOpen = bundleId ? isBundleExpanded(bundleId, memberCount) : true;

  useEffect(() => {
    pendingRef.current = selectedId;
    if (selectedId && bundleId && !latest.current.isBundleExpanded(bundleId, memberCount)) {
      latest.current.expandBundle(bundleId);
    }
    // Only a new selection expands; a later manual collapse must stick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId || pendingRef.current !== selectedId || !bundleOpen) return;
    // Wait a frame so the just-expanded bundle's rows are laid out.
    const raf = requestAnimationFrame(() => {
      if (pendingRef.current !== selectedId) return;
      scrollPlanRowIntoView(selectedId, reducedMotion);
      pendingRef.current = null;
    });
    return () => cancelAnimationFrame(raf);
  }, [selectedId, bundleOpen, reducedMotion]);
}
